import React from "react";
import ExcelJS from "exceljs";
import utils from "../../../utils/utils";
import {
  formatConditionLabel,
  formatReceiptStatusLabel,
  formatReviewReasonLabel,
  humanizeEpsfCode,
} from "./epsfBadges";

const AUDIT_KEY_LABELS = {
  status: "Status",
  receiptstatus: "Status",
  qty: "Quantity",
  quantity: "Quantity",
  batch: "Batch / Lot",
  batchnumber: "Batch / Lot",
  releasecomment: "Release comment",
  condition: "Condition",
  conditioncode: "Condition",
  kits: "Kits",
  kitcount: "Kits",
  temperature: "Temperature",
  storageoverride: "Storage override",
  reviewreason: "Hold reason",
};

function normalizeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

function formatQuantityDisplay(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  if (Number.isInteger(n)) return String(n);
  const rounded = Math.round(n * 10000) / 10000;
  if (Number.isInteger(rounded)) return String(rounded);
  return String(rounded).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
}

function formatAuditFieldValue(key, value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "—";
  const k = normalizeKey(key);
  if (k === "status" || k === "receiptstatus") {
    return formatReceiptStatusLabel(raw) || humanizeEpsfCode(raw) || raw;
  }
  if (k === "condition" || k === "conditioncode") {
    return formatConditionLabel(raw) || humanizeEpsfCode(raw) || raw;
  }
  if (k === "qty" || k === "quantity") {
    return formatQuantityDisplay(raw);
  }
  if (k === "reviewreason" || k === "storageoverride" || k === "releasecomment") {
    return formatReviewReasonLabel(raw) || humanizeEpsfCode(raw) || raw;
  }
  return humanizeEpsfCode(raw) || raw;
}

function formatAuditFieldLabel(key) {
  const k = normalizeKey(key);
  return AUDIT_KEY_LABELS[k] || humanizeEpsfCode(key) || key;
}

function inferAuditTitle(reason, columnName) {
  const r = String(reason || "").trim().toLowerCase();
  if (r.includes("receipt posted")) return "Receipt posted";
  if (r.includes("release")) return "Receipt released";
  if (r.includes("evidence")) return "Evidence uploaded";
  const col = String(columnName || "").trim();
  if (col) return humanizeEpsfCode(col);
  return formatReviewReasonLabel(reason) || "";
}

/**
 * Presentation-only formatter for audit NewValue / legacy packed strings.
 * Does not mutate stored audit history.
 */
export function formatAuditNewValueDisplay(value, { reason, columnName } = {}) {
  const raw = String(value ?? "").trim();
  if (!raw) return { kind: "empty", text: "—" };

  if (raw.includes("=") && (raw.includes(";") || /^(Status|Qty|Quantity|Batch)\s*=/i.test(raw))) {
    const parts = raw
      .split(";")
      .map((p) => p.trim())
      .filter(Boolean);
    const rows = parts.map((part) => {
      const eq = part.indexOf("=");
      if (eq < 0) {
        return { label: "Detail", value: humanizeEpsfCode(part) || part };
      }
      const key = part.slice(0, eq).trim();
      const val = part.slice(eq + 1).trim();
      return {
        label: formatAuditFieldLabel(key),
        value: formatAuditFieldValue(key, val),
      };
    });
    return {
      kind: "structured",
      title: inferAuditTitle(reason, columnName),
      rows,
    };
  }

  const statusLabel = formatReceiptStatusLabel(raw);
  if (statusLabel && normalizeKey(statusLabel) !== normalizeKey(raw)) {
    return { kind: "text", text: statusLabel };
  }
  const conditionLabel = formatConditionLabel(raw);
  if (conditionLabel && normalizeKey(conditionLabel) !== normalizeKey(raw)) {
    return { kind: "text", text: conditionLabel };
  }
  if (/^\d+(\.\d+)?$/.test(raw)) {
    return { kind: "text", text: formatQuantityDisplay(raw) };
  }
  const review = formatReviewReasonLabel(raw);
  if (review) return { kind: "text", text: review };
  return { kind: "text", text: humanizeEpsfCode(raw) || raw };
}

export function renderAuditActor(row) {
  const name = String(
    row?.CommittedByName || row?.committedByName || "",
  ).trim();
  const email = String(
    row?.CommittedByEmail || row?.committedByEmail || "",
  ).trim();
  const id = String(row?.CommittedBy || row?.committedBy || "").trim();
  if (name || email) {
    return (
      <div className="epsf-audit-actor" title={id || undefined}>
        <span className="epsf-audit-actor__name">
          {name || "Former/Unavailable user"}
        </span>
        {email ? (
          <span className="epsf-audit-actor__email">{email}</span>
        ) : null}
      </div>
    );
  }
  if (id) {
    return (
      <div className="epsf-audit-actor" title={id}>
        <span className="epsf-audit-actor__name">Former/Unavailable user</span>
      </div>
    );
  }
  return "—";
}

export function renderAuditNewValue(row) {
  const formatted = formatAuditNewValueDisplay(
    row?.NewValue ?? row?.newValue,
    {
      reason: row?.Reason || row?.reason,
      columnName: row?.ColumnName || row?.columnName,
    },
  );
  if (formatted.kind === "structured") {
    return (
      <div className="epsf-audit-value">
        {formatted.title ? (
          <div className="epsf-audit-value__title">{formatted.title}</div>
        ) : null}
        <dl className="epsf-audit-value__rows">
          {formatted.rows.map((r) => (
            <div key={`${r.label}-${r.value}`} className="epsf-audit-value__row">
              <dt>{r.label}</dt>
              <dd>{r.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }
  return <span className="text-break">{formatted.text}</span>;
}

export function formatAuditFieldLabelDisplay(columnName) {
  const raw = String(columnName || "").trim();
  if (!raw) return "—";
  return formatAuditFieldLabel(raw);
}

export function formatAuditReasonDisplay(reason) {
  return formatReviewReasonLabel(reason) || humanizeEpsfCode(reason) || "—";
}

function auditActorName(row) {
  const name = String(
    row?.CommittedByName || row?.committedByName || "",
  ).trim();
  if (name) return name;
  const email = String(
    row?.CommittedByEmail || row?.committedByEmail || "",
  ).trim();
  if (email) return email;
  return "Former/Unavailable user";
}

function parseAuditPairs(raw) {
  const text = String(raw ?? "").trim();
  if (!text) return {};
  if (!(text.includes("=") && (text.includes(";") || /^(Status|Qty|Quantity|Batch)\s*=/i.test(text)))) {
    return {};
  }
  const out = {};
  text
    .split(";")
    .map((p) => p.trim())
    .filter(Boolean)
    .forEach((part) => {
      const eq = part.indexOf("=");
      if (eq < 0) return;
      const key = normalizeKey(part.slice(0, eq).trim());
      out[key] = part.slice(eq + 1).trim();
    });
  return out;
}

function qtyLabel(raw, tracking) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return "";
  const qty = formatQuantityDisplay(raw);
  const isKit = normalizeKey(tracking) === "individualkit";
  if (isKit) return `${qty} kit${n === 1 ? "" : "s"}`;
  return `Qty ${qty}`;
}

/**
 * Expand raw audit rows into one timeline event per action
 * (release / return / destruction / receive / dispense / …).
 */
export function buildAuditTimelineEvents(rows) {
  const list = Array.isArray(rows) ? [...rows] : [];
  /* Newest first (latest event on top) */
  list.sort((a, b) => {
    const ta = new Date(a.TimeCommitted || a.timeCommitted || 0).getTime();
    const tb = new Date(b.TimeCommitted || b.timeCommitted || 0).getTime();
    return tb - ta;
  });
  const events = [];

  list.forEach((a, index) => {
    const reason = String(a.Reason || a.reason || "").trim();
    const columnName = String(a.ColumnName || a.columnName || "").trim();
    const newValue = a.NewValue ?? a.newValue;
    const col = normalizeKey(columnName);
    const reasonNorm = reason.toLowerCase();
    const pairs = parseAuditPairs(newValue);
    const when = utils.formatDateTimeDisplay(
      a.TimeCommitted || a.timeCommitted,
    );
    const who = auditActorName(a);
    const baseId = String(a.AuditId || a.auditId || index);
    const tracking = pairs.tracking || "";
    const comment =
      pairs.reviewcomment ||
      pairs.releasecomment ||
      "";

    const push = (
      idSuffix,
      title,
      {
        notes = [],
        badge = "",
        tone = "neutral",
        icon = "fa-clipboard-list",
      } = {},
    ) => {
      events.push({
        id: `${baseId}-${idSuffix}`,
        title,
        notes: Array.isArray(notes) ? notes.filter((n) => n && n.value) : [],
        badge,
        when: when || "—",
        who,
        tone,
        icon,
      });
    };

    /* Review held stock — may split into release + remaining disposition */
    if (
      col === "receiptstatus" ||
      reasonNorm.includes("review held")
    ) {
      const releasedQty = pairs.releasedqty;
      const remainingQty = pairs.remainingqty;
      const remainAction = normalizeKey(pairs.remainingaction || "");
      const released = Number(releasedQty);
      let added = false;

      if (Number.isFinite(released) && released > 0) {
        push("release", "Kit released", {
          badge: "Released",
          tone: "success",
          icon: "fa-check-circle",
          notes: [
            { key: "Qty", value: qtyLabel(releasedQty, tracking) },
            { key: "Comment", value: comment },
          ],
        });
        added = true;
      }

      if (remainAction === "returnpending") {
        push("return", "Kit return to sponsor / depot", {
          badge: "Return",
          tone: "warning",
          icon: "fa-undo",
          notes: [
            { key: "Qty", value: qtyLabel(remainingQty, tracking) },
            { key: "Comment", value: comment },
          ],
        });
        added = true;
      } else if (remainAction === "destructionpending") {
        push("destroy", "Kit sent for destruction", {
          badge: "Destruction",
          tone: "danger",
          icon: "fa-trash-alt",
          notes: [
            { key: "Qty", value: qtyLabel(remainingQty, tracking) },
            { key: "Comment", value: comment },
          ],
        });
        added = true;
      }

      if (!added) {
        push("review", "Held stock reviewed", {
          badge: "Review",
          notes: [{ key: "Comment", value: comment || formatAuditReasonDisplay(reason) }],
        });
      }
      return;
    }

    /* Receive */
    if (
      col === "kitshipmentreceipt" ||
      col === "receipt" ||
      col === "kitnumbers" ||
      reasonNorm.includes("kit shipment receipt") ||
      reasonNorm.includes("individual kit receipt") ||
      reasonNorm.includes("receipt posted")
    ) {
      const kitCount = pairs.kitcount || pairs.qty || pairs.quantity;
      const qty = qtyLabel(kitCount, pairs.tracking || "IndividualKit");
      const statusLabel = formatReceiptStatusLabel(pairs.status);
      push("receive", "Kit received", {
        badge: "Received",
        tone: "info",
        icon: "fa-box-open",
        notes: [
          qty ? { key: "Qty", value: qty } : null,
          statusLabel ? { key: "Status", value: statusLabel } : null,
        ],
      });
      return;
    }

    /* Ledger movement — newValue may be plain TxnType or key=value pairs */
    if (col === "txntype" || reasonNorm.includes("ledger movement")) {
      const txn = normalizeKey(
        pairs.txntype ||
          pairs.status ||
          String(newValue ?? "")
            .split(";")[0]
            .trim(),
      );
      const relatedQty =
        a.RelatedTxnQuantity ?? a.relatedTxnQuantity ?? null;
      const qtyRaw =
        pairs.kitcount || pairs.qty || pairs.quantity || relatedQty;
      const tracking =
        pairs.tracking ||
        (qtyRaw != null && qtyRaw !== "" ? "IndividualKit" : "");
      const qty = qtyLabel(qtyRaw, tracking || "IndividualKit");
      const notesText =
        pairs.notes ||
        a.RelatedTxnNotes ||
        a.relatedTxnNotes ||
        "";
      const ledgerNotes = [
        qty ? { key: "Qty", value: qty } : null,
        notesText ? { key: "Notes", value: String(notesText) } : null,
      ];

      if (txn === "dispense") {
        push("dispense", "Kit dispensed", {
          badge: "Dispensed",
          tone: "info",
          icon: "fa-hand-holding-medical",
          notes: ledgerNotes,
        });
        return;
      }
      if (txn === "return") {
        push("participant-return", "Kit return from participant", {
          badge: "Return",
          tone: "warning",
          icon: "fa-user-check",
          notes: ledgerNotes,
        });
        return;
      }
      if (txn === "stockreturn") {
        push("stock-return", "Kit return to sponsor / depot", {
          badge: "Return",
          tone: "warning",
          icon: "fa-undo",
          notes: ledgerNotes,
        });
        return;
      }
      if (txn === "destroy") {
        push("destroyed", "Kit destroyed", {
          badge: "Destroyed",
          tone: "danger",
          icon: "fa-trash-alt",
          notes: ledgerNotes,
        });
        return;
      }
    }

    if (reasonNorm.includes("evidence")) {
      push("evidence", "Evidence uploaded", {
        badge: "Evidence",
        notes: [{ key: "Type", value: String(newValue || "") }],
        icon: "fa-paperclip",
      });
      return;
    }

    if (reasonNorm.includes("storage compatibility override")) {
      push("override", "Storage override", {
        badge: "Override",
        tone: "warning",
        icon: "fa-exclamation-triangle",
        notes: [{ key: "Reason", value: formatAuditReasonDisplay(reason) }],
      });
      return;
    }

    /* Fallback — one card, short title */
    const formatted = formatAuditNewValueDisplay(newValue, { reason, columnName });
    const title =
      (formatted.kind === "structured" && formatted.title) ||
      inferAuditTitle(reason, columnName) ||
      formatAuditFieldLabelDisplay(columnName) ||
      "Activity";
    const notes =
      formatted.kind === "structured"
        ? formatted.rows.slice(0, 3).map((r) => ({ key: r.label, value: r.value }))
        : formatted.kind === "text" && formatted.text && formatted.text !== "—"
          ? [{ key: "Detail", value: formatted.text }]
          : [];
    push("fallback", title, { notes });
  });

  return events;
}

/**
 * Same card chrome as fridge temperature / stored-receipt logs.
 */
export function renderAuditLogCards(rows, { emptyMessage } = {}) {
  const events = buildAuditTimelineEvents(rows);
  if (!events.length) {
    return (
      <p className="epsf-storage-detail-list__empty">
        {emptyMessage || "No audit records yet."}
      </p>
    );
  }

  return events.map((ev) => {
    const qtyNote = (ev.notes || []).find(
      (n) => normalizeKey(n.key) === "qty",
    );
    const otherNotes = (ev.notes || []).filter(
      (n) => normalizeKey(n.key) !== "qty",
    );
    const hasNotes = Boolean(qtyNote || otherNotes.length);

    return (
      <article
        key={ev.id}
        className="epsf-storage-log-card"
        role="listitem"
      >
        <div className="epsf-storage-log-card__head">
          <div className="epsf-storage-log-card__title-wrap">
            <span
              className={`epsf-storage-log-card__icon is-audit is-${ev.tone}`}
              aria-hidden="true"
            >
              <i className={`fas ${ev.icon}`} />
            </span>
            <h6 className="epsf-storage-log-card__title">{ev.title}</h6>
          </div>
          {ev.badge ? (
            <span
              className={`epsf-storage-log-card__badge is-${
                ev.tone === "neutral" ? "neutral" : ev.tone
              }`}
            >
              {ev.badge}
            </span>
          ) : null}
        </div>
        {hasNotes ? (
          <div className="epsf-storage-log-card__notes">
            {qtyNote ? (
              <div className="epsf-storage-log-card__qty">
                <div className="epsf-storage-log-card__qty-col">
                  <span className="epsf-storage-log-card__qty-label">
                    {qtyNote.key}
                  </span>
                  <span className="epsf-storage-log-card__qty-value">
                    {qtyNote.value}
                  </span>
                </div>
              </div>
            ) : null}
            {otherNotes.map((n) => (
              <p
                key={`${n.key}-${n.value}`}
                className="epsf-storage-log-card__notes-line"
              >
                <span className="epsf-storage-log-card__notes-key">
                  {n.key}:
                </span>{" "}
                {n.value}
              </p>
            ))}
          </div>
        ) : null}
        <div className="epsf-storage-log-card__meta">
          <span className="epsf-storage-log-card__meta-item">
            <i className="far fa-clock" aria-hidden="true" />
            {ev.when}
          </span>
          <span className="epsf-storage-log-card__meta-item">
            <i className="far fa-user" aria-hidden="true" />
            {ev.who}
          </span>
        </div>
      </article>
    );
  });
}

/**
 * Export audit timeline events to Excel (newest first).
 * @param {object[]} rows raw audit rows
 * @param {{ fileName?: string }} [options]
 */
export async function exportAuditLogToExcel(rows, options = {}) {
  const events = buildAuditTimelineEvents(rows);
  const wb = new ExcelJS.Workbook();
  wb.creator = "AQ Trials ePSF";
  const ws = wb.addWorksheet("Audit log", {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  ws.columns = [
    { header: "When", key: "when", width: 22 },
    { header: "Action", key: "title", width: 36 },
    { header: "Status", key: "badge", width: 16 },
    { header: "Details", key: "details", width: 48 },
    { header: "By", key: "who", width: 22 },
  ];
  const header = ws.getRow(1);
  header.font = { bold: true };
  header.alignment = { vertical: "middle" };

  events.forEach((ev) => {
    const details = (ev.notes || [])
      .map((n) => `${n.key}: ${n.value}`)
      .join("; ");
    ws.addRow({
      when: ev.when || "",
      title: ev.title || "",
      badge: ev.badge || "",
      details,
      who: ev.who || "",
    });
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download =
    options.fileName ||
    `ePSF_Receipt_Audit_${new Date().toISOString().slice(0, 10)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
  return { success: true, count: events.length };
}
